import { z } from 'zod';
import { LotIdSchema, PlayerIdSchema } from '../ids';

/**
 * Where a character can be: the continuous world (ADR 0031). There is one place; areas of
 * simulation are an internal division and never a place a player travels to.
 */
export const SpaceSchema = z.object({ kind: z.literal('world') });
export type Space = z.infer<typeof SpaceSchema>;

export const WORLD_SPACE: Space = { kind: 'world' };

/**
 * The world layouts (ADR 0060): which authored content the server lays on the plane, `test` (the
 * Rethar Valley) or `launch` (the continent). Its clients lay the same.
 */
export const WORLD_LAYOUT_IDS = ['test', 'launch'] as const;
export const WorldLayoutIdSchema = z.enum(WORLD_LAYOUT_IDS);
export type WorldLayoutId = z.infer<typeof WorldLayoutIdSchema>;

/** Stable string form of a place, used as a map key for live instances. */
export const spaceKey = (place: Space): string => place.kind;

export const sameSpace = (a: Space, b: Space): boolean => spaceKey(a) === spaceKey(b);

/** A world rectangle in whole metres. */
export const WorldRectSchema = z.object({
  x: z.number().int(),
  y: z.number().int(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type WorldRect = z.infer<typeof WorldRectSchema>;

/** A point of the world plane, in metres. */
export const WorldPointSchema = z.object({ x: z.number(), y: z.number() });
export type WorldPoint = z.infer<typeof WorldPointSchema>;

/**
 * A recognizable place of the world shown in the places panel: an authored place (its layout
 * id is also the i18n key `scenes.<id>`), where it stands and where a walker arrives in it.
 */
export const LandmarkSchema = z.object({
  id: z.string(),
  rect: WorldRectSchema,
  entry: WorldPointSchema,
});
export type Landmark = z.infer<typeof LandmarkSchema>;

/**
 * A place of a region's trace (WORLD-TRACE: a site, a landmark, a hamlet) the character knows
 * (WORLD-DISCOVERY): its point and its circle, in world tiles.
 */
export const TracedPlaceSchema = z.object({
  id: z.string(),
  x: z.number(),
  y: z.number(),
  radius: z.number().min(0),
});
export type TracedPlace = z.infer<typeof TracedPlaceSchema>;

/**
 * A character's fixed lot (C9, ADR 0068: its town become a lot, where its provisional plot was), its
 * owner and its entry.
 */
export const PlotSchema = z.object({
  lotId: LotIdSchema,
  owner: z.object({ playerId: PlayerIdSchema, displayName: z.string() }),
  rect: WorldRectSchema,
  entry: WorldPointSchema,
});
export type Plot = z.infer<typeof PlotSchema>;
